import {useSettingsStore} from "@project/app-state";
import {
  RealtimeEventRouter,
  ResourceChangeRealtimeRouter,
  createQueryClient,
  getSessionQueryOptions,
} from "@project/application";
import {usersRealtimeHandler} from "@project/application/admin";
import {materialFunctionsRealtimeHandler} from "@project/application/engineering/material-functions";
import {supplierFunctionsRealtimeHandler} from "@project/application/engineering/supplier-functions";
import {serviceFunctionsRealtimeHandler} from "@project/application/engineering/service-functions";
import {suppliersRealtimeHandler} from "@project/application/engineering/suppliers";
import {servicesRealtimeHandler} from "@project/application/engineering/services";
import {supplierOffersRealtimeHandler} from "@project/application/engineering/supplier-offers";
import {leadsRealtimeHandler} from "@project/application/sales/leads";
import {representativesRealtimeHandler} from "@project/application/sales/representatives";
import {RootProviders} from "@project/providers";
import {createWebPersister} from "@project/query";
import {createAppRouter} from "@project/router";
import {Suspense, lazy} from "react";

/**
 * Stable module-level instances — created once, never re-created on render.
 */
const queryClient = createQueryClient();
const realtimeRouter = new RealtimeEventRouter(queryClient, {
  users: usersRealtimeHandler,
});
const resourceChangeRealtimeRouter = new ResourceChangeRealtimeRouter(queryClient, {
  "engineering.material-functions": materialFunctionsRealtimeHandler,
  "engineering.supplier-functions": supplierFunctionsRealtimeHandler,
  "engineering.service-functions": serviceFunctionsRealtimeHandler,
  "engineering.suppliers": suppliersRealtimeHandler,
  "engineering.services": servicesRealtimeHandler,
  "engineering.supplier-offers": supplierOffersRealtimeHandler,
  "sales.leads": leadsRealtimeHandler,
  "sales.representatives": representativesRealtimeHandler,
});

/**
 * Wire the web-specific per-query persister globally.
 * The persisterFn wraps every queryFn; the filters.predicate inside the persister
 * ensures only `persistent`-tier queries (gcTime ≥ 24 h) are written to localStorage.
 * Kept here (not in createQueryClient) so the application package stays platform-agnostic.
 */
const {persisterFn} = createWebPersister();

queryClient.setDefaultOptions({
  queries: {
    ...queryClient.getDefaultOptions().queries,
    persister: persisterFn,
  },
});

const router = createAppRouter({
  auth: {
    getSession: () => queryClient.ensureQueryData(getSessionQueryOptions()),
  },
  queryClient,
  realtime: {
    connect: (options) => realtimeRouter.connect(options),
    disconnect: () => realtimeRouter.disconnect(),
  },
  resourceChangeRealtime: {
    connect: (options) => resourceChangeRealtimeRouter.connect(options),
    disconnect: () => resourceChangeRealtimeRouter.disconnect(),
  },
});

// Dev-only: zero production bundle cost via dynamic import and Vite tree-shaking.
// Rendered directly in App (not via provider) so it's inside StrictMode
// and can receive router/queryClient as stable references from this module.
const DevtoolsShell = import.meta.env.DEV
  ? lazy(() => import("./devtools/devtools-shell").then((m) => ({default: m.DevtoolsShell})))
  : () => null;

export default function App() {
  const isDevModeEnabled = useSettingsStore((s) => s.isDevModeEnabled);

  return (
    <>
      <RootProviders queryClient={queryClient} router={router} />
      {isDevModeEnabled && import.meta.env.DEV && (
        <Suspense key="devtools">
          <DevtoolsShell queryClient={queryClient} router={router} />
        </Suspense>
      )}
    </>
  );
}
